import type { Block, LayoutFn, Line, Segment, Theme } from "./base";
import { normalizedWidth } from "./base";
import { pad, padLine, wrapText } from "./text";

export function statusbar(
  left: Segment[],
  right: Segment[],
  opts?: { bg?: string; color?: string; theme?: Theme },
): LayoutFn {
  return (w) => {
    const rightLen = right.reduce(
      (acc, s) => acc + (typeof s === "string" ? s.length : s.text.length),
      0,
    );
    const leftWidth = Math.max(0, w - rightLen);
    const paddedLeft = padLine(left, leftWidth);

    const theme = opts?.theme;
    const bg = opts?.bg ?? theme?.components.statusbar.bg;
    const color = opts?.color ?? theme?.components.statusbar.fg;
    const line: Line = [...paddedLeft, ...right].map((seg) => {
      if (typeof seg === "string") {
        return bg || color ? { text: seg, bg, color } : seg;
      }
      return { ...seg, bg: seg.bg ?? bg, color: seg.color ?? color };
    });
    return [padLine(line, w)];
  };
}

export function tabs(
  names: string[],
  active: number,
  opts?: {
    activeColor?: string;
    activeBg?: string;
    inactiveColor?: string;
    separatorColor?: string;
    onSelect?: (index: number) => void;
    onClicks?: ((() => void) | undefined)[];
    theme?: Theme;
  },
): LayoutFn {
  return (w) => {
    const line: Line = [];
    const onSelect = opts?.onSelect;
    const t = opts?.theme;
    for (let i = 0; i < names.length; i++) {
      const isActive = i === active;
      const name = names[i]!;
      const label = " " + (isActive ? name.toUpperCase() : name) + " ";
      const onClick = opts?.onClicks?.[i] ?? (onSelect ? () => onSelect(i) : undefined);

      if (isActive) {
        line.push({
          text: label,
          color: opts?.activeColor ?? t?.components.tabs.activeFg,
          bg: opts?.activeBg ?? t?.components.tabs.activeBg,
          onClick,
        });
      } else {
        line.push({
          text: label,
          color: opts?.inactiveColor ?? t?.components.tabs.inactiveFg,
          onClick,
        });
      }

      if (i < names.length - 1) {
        line.push({ text: "\u2502", color: opts?.separatorColor ?? t?.components.tabs.separator, noSelect: true });
      }
    }
    return [padLine(line, w)];
  };
}

export function badge(
  label: string,
  opts?: { color?: string; bg?: string; onClick?: () => void; theme?: Theme },
): Segment {
  const t = opts?.theme;
  return {
    text: label,
    color: opts?.color ?? t?.components.badge.fg,
    bg: opts?.bg ?? t?.components.badge.bg,
    onClick: opts?.onClick,
  };
}

export type KVPair = {
  key: string;
  value: string | Segment[];
  keyColor?: string;
  valueColor?: string;
  onClick?: () => void;
};

export function kv(
  pairs: KVPair[],
  opts?: { separator?: string; keyColor?: string; valueColor?: string; keyWidth?: number },
): LayoutFn {
  return (w) => {
    const width = normalizedWidth(w);
    const sep = opts?.separator ?? "  ";
    const maxKey = opts?.keyWidth ?? Math.max(0, ...pairs.map((p) => p.key.length));
    const result: Line[] = [];

    for (const pair of pairs) {
      const keyText = pad(pair.key, maxKey);
      const keyColor = pair.keyColor ?? opts?.keyColor;
      const keySeg: Segment = keyColor ? { text: keyText, color: keyColor } : keyText;

      if (typeof pair.value === "string") {
        const valColor = pair.valueColor ?? opts?.valueColor;
        const valSeg: Segment =
          valColor || pair.onClick
            ? { text: pair.value, color: valColor, onClick: pair.onClick }
            : pair.value;
        result.push(padLine([keySeg, sep, valSeg], width));
      } else {
        result.push(padLine([keySeg, sep, ...pair.value], width));
      }
    }

    return result;
  };
}

export function callout(
  content: LayoutFn | string,
  opts?: { color?: string; accent?: string; label?: string },
): LayoutFn {
  return (w) => {
    const width = normalizedWidth(w);
    const accent = opts?.accent ?? opts?.color;
    const innerWidth = Math.max(1, width - 2);

    let block: Block;
    if (typeof content === "string") {
      const wrapped = wrapText(content, innerWidth);
      block = wrapped.map((line) => {
        const seg: Line = opts?.color ? [{ text: line, color: opts.color }] : [line];
        return padLine(seg, innerWidth);
      });
    } else {
      block = content(innerWidth);
    }

    const result: Block = [];

    if (opts?.label) {
      const labelLine: Line = [
        { text: "\u2590", color: accent, noSelect: true },
        " ",
        { text: opts.label, color: accent, bold: true },
      ];
      result.push(padLine(labelLine, width));
    }

    for (const line of block) {
      result.push([
        { text: "\u2590", color: accent, noSelect: true },
        " ",
        ...padLine(line, innerWidth),
      ]);
    }

    return result;
  };
}

export function checkbox(
  checked: boolean,
  label: string,
  opts?: { color?: string; checkColor?: string; onClick?: () => void },
): LayoutFn {
  return (w) => {
    const width = normalizedWidth(w);
    const check = checked ? "\u2713" : " ";
    const boxSeg: Segment = {
      text: `[${check}]`,
      color: opts?.checkColor ?? opts?.color,
      onClick: opts?.onClick,
    };
    const labelSeg: Segment = opts?.onClick
      ? { text: ` ${label}`, color: opts.color, onClick: opts.onClick }
      : opts?.color
        ? { text: ` ${label}`, color: opts.color }
        : ` ${label}`;
    return [padLine([boxSeg, labelSeg], width)];
  };
}

export function inputField(
  value: string,
  opts?: {
    placeholder?: string;
    width?: number;
    focused?: boolean;
    color?: string;
    borderColor?: string;
    onClick?: () => void;
  },
): LayoutFn {
  return (w) => {
    const totalWidth = normalizedWidth(opts?.width ?? w);
    const innerWidth = Math.max(1, totalWidth - 2);
    const bc = opts?.borderColor;
    const focused = opts?.focused ?? false;

    const display = value || opts?.placeholder || "";
    const isPlaceholder = !value && opts?.placeholder;

    const textTruncated =
      display.length > innerWidth
        ? display.slice(display.length - innerWidth)
        : display;
    const padded = pad(textTruncated + (focused ? "\u2588" : ""), innerWidth);

    const line: Line = [
      { text: focused ? "\u2502" : "[", color: bc, noSelect: true },
      isPlaceholder
        ? { text: padded, dim: true, color: opts?.color, onClick: opts?.onClick }
        : opts?.color || opts?.onClick
          ? { text: padded, color: opts?.color, onClick: opts?.onClick }
          : padded,
      { text: focused ? "\u2502" : "]", color: bc, noSelect: true },
    ];
    return [padLine(line, w)];
  };
}
